import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Button, Modal, Progress, Typography } from "antd";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import ProfileCompact from "@Components/user/ProfileCompact";
import ActivitySearch from "@Components/activity/ActivitySearch";
import { IActivitySearch, ISearchTerms } from "@Components/activity/types";
import SessionManager from "@Core/session";
import ActivityService from "@Services/ActivityService";
import { IActivityDefinitions } from "@Models/IActivityDetails";
import { IUserConnectionDetails } from "@Models/IUserConnectionDetails";
import { IModalState, QueryKeys } from "@Constants";
import PageHelmet from "@Components/shared/PageHelmet";
import { useConnectionsQuery, useUserAccountDetailsQuery } from "@Components/shared/queries";
import ActivityUpload from "@Components/activity/ActivityUpload";
import { isDevelopment, useSearchParams } from "@Utils";
import "@Styles/pages/activityPage.scss";
import GuidanceDrawer from "@Components/shared/GuidanceDrawer";
import { getTutorialTip } from "@Components/assessment/helpers";
import useActivityExport from "@Components/activity/hooks/useActivityExport";
import { DownloadOutlined, KeyboardArrowRight, UploadOutlined } from "@mui/icons-material";
import IconButton from "@Components/shared/IconButton";
import useCurrentActivityUpload from "@Components/activity/hooks/useCurrentActivityUpload";
import ActivityDetailsPage from "../activity/ActivityDetails";
import { useActivityCategoriesTree } from "@Components/shared/hooks/useActivityCategoriesTree";

const BLANK_SEARCH_TERMS = { activityTerms: [], dateTerms: [] };

const ActivityPage: React.FC = () => {
    const location = useLocation();
    const { userId: loggedInUserId } = SessionManager.getServiceUser();
    const [searchParams] = useSearchParams();
    const [searching, setSearching] = useState<boolean>(false);
    const [currentUserId, setCurrentUserId] = useState<string>(loggedInUserId);
    const [connections, setConnections] = useState<IUserConnectionDetails[]>([]);
    const [terms, setTerms] = useState<ISearchTerms>({ ...BLANK_SEARCH_TERMS });
    const [currentActivityId, setCurrentActivityId] = useState<string>("");
    const history = useHistory();
    const queryClient = useQueryClient();
    const urlParamUserId = new URLSearchParams(location.search).get("user");
    const urlParamActivityId = searchParams.get("activity");
    const isLoggedInUserPage = currentUserId === loggedInUserId;

    const activityCategoriesTree = useActivityCategoriesTree();
    const customActivityCategory = activityCategoriesTree.find((category) => category.title === "Custom");

    const customActivityCategoryId =
        customActivityCategory && customActivityCategory.value.includes("cat-")
            ? customActivityCategory.value.replace("cat-", "")
            : null;

    const customActivityId = customActivityCategory?.children?.find((category) => category.title === "Custom")?.value;

    const { products } = queryClient?.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);
    const canUpload =
        isDevelopment() && urlParamUserId === loggedInUserId && products?.some((product) => product.name === "custom");

    const resetTerms = useCallback(() => {
        setCurrentActivityId("");
        history.push(`/activity?user=${SessionManager.getServiceUser()?.userId}`);
        setCurrentUserId(SessionManager.getServiceUser()?.userId);
        setTerms({ ...BLANK_SEARCH_TERMS });
    }, [history]);

    const clearCurrentActivity = useCallback(async () => {
        setCurrentActivityId("");
        setCurrentUserId(SessionManager.getServiceUser()?.userId);
        await queryClient.invalidateQueries([QueryKeys.ActivityCurrent]);
    }, []);

    const getCurrentTerms = useCallback(async () => {
        setTerms({ ...BLANK_SEARCH_TERMS });

        if (urlParamUserId !== null) {
            if (urlParamUserId !== currentUserId) {
                setCurrentUserId(urlParamUserId);
            }
        } else {
            // There must be a user provided, so if this is missing use the logged in user's id
            resetTerms();
            return;
        }

        if (urlParamActivityId !== null) {
            setCurrentActivityId(urlParamActivityId);
        }
    }, [currentUserId, resetTerms, urlParamActivityId, urlParamUserId]);

    /* ---------- Queries & Mutations ------------- */

    const userAccountDetailsQuery = useUserAccountDetailsQuery();

    const activityListQuery = useInfiniteQuery(
        [QueryKeys.ActivitySearchResults, currentUserId],
        async ({ pageParam = -1 }) => {
            const activityService = new ActivityService();
            const result = await activityService.getUser(
                isLoggedInUserPage && connectionsResult.length
                    ? connectionsResult?.map((user) => user.userId)
                    : [urlParamUserId],
                terms.activityTerms,
                [],
                terms?.dateTerms[0],
                terms?.dateTerms[1],
                pageParam,
                false,
                isLoggedInUserPage,
                isLoggedInUserPage ? currentUserId : null
            );
            return result.hasErrors ? null : result.value;
        },
        {
            getNextPageParam: (payload) => (payload?.nextPage == -1 ? null : payload?.nextPage),
            enabled: false,
        }
    );

    const { data: connectionsResult } = useConnectionsQuery();
    useEffect(() => setConnections(connectionsResult), [connectionsResult]);

    /* ---------- Handlers ------------- */

    /**
     * Search activities using the criteria provided.
     * @param values
     */
    const handleSearchSelect = async (values: IActivitySearch) => {
        const newTerms: ISearchTerms = {};
        newTerms.dateTerms = [];
        newTerms.activityTerms = [];
        if (
            values !== null &&
            values.dateTerms !== undefined &&
            values.dateTerms !== null &&
            values.dateTerms[0] !== null &&
            values.dateTerms[1] !== null
        ) {
            newTerms.dateTerms[0] = values?.dateTerms[0]?.toISOString();
            newTerms.dateTerms[1] = values?.dateTerms[1]?.toISOString();
        } else {
            newTerms.dateTerms[0] = "";
            newTerms.dateTerms[1] = "";
        }
        values?.activityTerms?.forEach((x) => {
            newTerms.activityTerms.push(x.toString());
        });

        setTerms(newTerms);
        setCurrentActivityId("");
        history.push(`/activity?user=${currentUserId}`);
        search();
    };

    /**
     * Open an activity on select.
     * @param userId
     * @param activityInstanceId
     */
    const handleActivitySelect = useCallback((userId: string, activityInstanceId: string) => {
        setCurrentActivityId(activityInstanceId);
        setCurrentUserId(userId);
        history.push(`/activity?user=${userId}&activity=${activityInstanceId}`);
    }, []);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const showModal = async () => {
        await clearCurrentActivity();
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    /* ---------- Hooks ------------- */

    useEffect(() => {
        const checkUrlTerms = async () => {
            await getCurrentTerms();
        };
        checkUrlTerms().then();
    }, [getCurrentTerms, urlParamUserId]);

    const search = () => {
        const refetch = async () => {
            setSearching(true);
            await queryClient.resetQueries([QueryKeys.ActivitySearchResults, currentUserId]);
            await activityListQuery.refetch();
            setSearching(false);
        };
        if (!searching) {
            refetch().catch(() => {
                //
            });
        }
    };

    const getProfileCompact = () => {
        const useSelf = currentUserId === SessionManager.getServiceUser()?.userId;
        let render = false;
        let connection: IUserConnectionDetails;
        if (!useSelf) {
            connection = connections?.find((x) => x.userId === currentUserId);
            render = connections?.findIndex((x) => x.userId === currentUserId) !== -1;
        }
        return (
            <ProfileCompact
                render={
                    useSelf
                        ? userAccountDetailsQuery.data !== undefined && userAccountDetailsQuery.data !== null
                        : render
                }
                fullName={useSelf ? userAccountDetailsQuery.data?.fullName : render ? connection?.fullName : ""}
                countryCode={
                    useSelf ? userAccountDetailsQuery.data?.countryCode : render ? connection?.countryCode : ""
                }
                organisation={
                    useSelf ? userAccountDetailsQuery.data?.organisation : render ? connection?.organisation : ""
                }
                connectionDate={connection?.connectionDate}
            />
        );
    };

    const activities =
        activityListQuery.data?.pages[0]?.activities?.length !== undefined
            ? activityListQuery?.data?.pages?.flatMap((x) => x?.activities)
            : [];

    const activityInstanceIds = activities?.map((a) => a.activityInstanceId) ?? [];

    const { dateTerms = [] } = terms;
    const [startDate, endDate] = dateTerms;

    const metadataRequest = { activityInstanceIds, endDate, ownerId: currentUserId, startDate };
    const { handleExport, isExporting } = useActivityExport(activities, metadataRequest);
    const renderResults = activities !== undefined && activities !== null && activities?.length > 0;

    const { modalState, percentage } = useCurrentActivityUpload();

    const BUTTON_STATES: Record<
        IModalState,
        { text: string; icon: JSX.Element; children?: (JSX.Element | string)[]; style?: React.CSSProperties }
    > = {
        standard: {
            text: "Upload video",
            icon: <UploadOutlined />,
        },
        continue: {
            text: "Continue video upload",
            icon: <KeyboardArrowRight />,
        },
        uploading: {
            text: "Upload video",
            children: [
                <Progress percent={percentage} size="small" style={{ width: "150px" }} showInfo={false} />,
                <Typography.Text>View</Typography.Text>,
            ],
            icon: <KeyboardArrowRight />,
        },
        complete: {
            text: "Video upload complete",
            children: [<Typography.Text>View</Typography.Text>],
            icon: <KeyboardArrowRight />,
        },
        error: {
            text: "Video upload error",
            children: [<Typography.Text style={{ color: "black" }}>Try again</Typography.Text>],
            icon: <KeyboardArrowRight />,
            style: { color: "red", borderColor: "red" },
        },
    };

    return (
        <>
            {currentActivityId ? (
                <ActivityDetailsPage activities={activities} clearCurrentActivity={clearCurrentActivity} />
            ) : (
                <article className="activity-page">
                    <PageHelmet name={"Activity"} />
                    <div
                        style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center" }}
                    >
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            {canUpload ? (
                                <>
                                    <IconButton
                                        style={{ ...BUTTON_STATES[modalState.status]?.style, display: "flex" }}
                                        className="secondary border-grey"
                                        position="right"
                                        icon={BUTTON_STATES[modalState.status].icon}
                                        onClick={showModal}
                                    >
                                        <div style={{ marginRight: "20px" }}>
                                            {BUTTON_STATES[modalState.status].text}
                                        </div>
                                        <div className="flex" style={{ gap: "20px" }}>
                                            {BUTTON_STATES[modalState.status].children?.map((child) => (
                                                <>{child}</>
                                            ))}
                                        </div>
                                    </IconButton>
                                    <Modal
                                        className="activity-upload-modal"
                                        width={700}
                                        title={null}
                                        visible={isModalOpen}
                                        onCancel={handleCancel}
                                        footer={null}
                                        closable={false}
                                    >
                                        <ActivityUpload
                                            handleClose={handleCancel}
                                            customActivityId={customActivityId}
                                            customActivityCategoryId={customActivityCategoryId}
                                        />
                                    </Modal>
                                </>
                            ) : null}
                            {isDevelopment() && renderResults ? (
                                <Button className="secondary flex" loading={isExporting} onClick={handleExport}>
                                    Download Data
                                    <DownloadOutlined />
                                </Button>
                            ) : null}
                        </div>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            {getProfileCompact()}
                            <div className="guidance-container">
                                <GuidanceDrawer text={getTutorialTip("activity")} />
                            </div>
                        </div>
                    </div>
                    <ActivitySearch
                        userId={currentUserId}
                        terms={terms}
                        handleSearchSelect={handleSearchSelect}
                        activityCategoriesTree={activityCategoriesTree}
                        isSearching={searching}
                        hasNextPage={activityListQuery?.hasNextPage}
                        isNextPageLoading={activityListQuery?.isFetchingNextPage}
                        loadNextPage={activityListQuery?.fetchNextPage}
                        handleActivitySelect={handleActivitySelect}
                        activities={activities}
                    />
                </article>
            )}
        </>
    );
};
export default ActivityPage;
